import { Document, Page, pdf, StyleSheet, Text, View } from '@react-pdf/renderer';
import React from 'react';

import type { User } from '../../../@types/User';
import type { DashboardEventActivation, DashboardEventPositions, DashboardExactDates, DashboardPersonAssignments } from '../../../store/dashboardSlice';

const MOIS_COURTS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

const MONTHS_LOWER = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

export interface PlanningPdfProps {
  companyName: string;
  year: number;
  startMonth: number;
  eventPositions: DashboardEventPositions;
  eventActivation: DashboardEventActivation;
  personAssignments: DashboardPersonAssignments;
  exactDates: DashboardExactDates;
  rowVisibility: Record<string, boolean>;
  companyUsers: User[];
  referentSignataires?: User[];
  dpeQualificationManagers?: User[];
  agDocumentManagers?: User[];
}

function formatUserName(user: User): string {
  return `${user.firstname} ${user.lastname ? user.lastname.toUpperCase() : ''}`.trim();
}

function formatUserList(users: User[] | undefined): string {
  if (!users || users.length === 0) { return 'Aucun'; }
  return users.map(formatUserName).join(', ');
}

function pluralizeFr(singular: string, plural: string, users: User[] | undefined): string {
  return (users?.length ?? 0) >= 2 ? plural : singular;
}

const SIGNATAIRES_LABEL = { singular: 'Signataire des envois', plural: 'Signataires des envois' };
const CHARGE_QUALIF_LABEL = { singular: 'Chargé de qualification', plural: 'Chargés de qualification' };
const RESPONSABLE_AG_LABEL = { singular: 'Responsable du document d\u2019AG', plural: 'Responsables du document d\u2019AG' };

interface RowConfig {
  key: string;
  label: string;
  eventType?: 'newsletter' | 'referent' | 'sci-referent' | 'boitage' | 'followup';
  continuous?: boolean;
  continuousLabel?: string;
}

const ROW_CONFIGS: RowConfig[] = [
  { key: 'newsletter', label: 'Envoi Newsletter Trimestriel', eventType: 'newsletter' },
  { key: 'referent-mails', label: 'Envoi de mails présentation du référent', eventType: 'referent' },
  { key: 'ag-communication', label: 'Communication lors des AG', continuous: true, continuousLabel: 'Documents et argumentaire AG' },
  { key: 'dpe', label: 'DPE (traitement à la semaine)', continuous: true, continuousLabel: 'Détection automatique chaque mercredi' },
  { key: 'sci-mails', label: 'Envois de mails aux SCI (transaction / gestion)', eventType: 'sci-referent' },
  { key: 'boitage', label: 'BOITAGE dans les copropriétés à fort potentiel', eventType: 'boitage' },
  { key: 'property-communication', label: 'Communication sur vos biens à vendre', continuous: true, continuousLabel: 'Information aux copropriétaires' },
  { key: 'follow-up-meetings', label: 'Points d\u2019avancement avec Déclarations Juridiques', eventType: 'followup' },
];

const A4_LANDSCAPE: [number, number] = [841.89, 595.28];
const A4_PORTRAIT = 'A4';

const BRAND_BLUE = '#134094';
const HEADER_BG = '#134094';
const ACCENT_BLUE = '#0070C0';
const GREEN = '#059669';
const VIOLET = '#7c3aed';

const TEAL = '#0d9488';

const TYPE_COLORS: Record<string, string> = {
  newsletter: GREEN,
  referent: GREEN,
  'sci-referent': VIOLET,
  boitage: VIOLET,
  followup: TEAL,
};

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    paddingTop: 30,
    paddingBottom: 40,
    paddingHorizontal: 30,
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#1f2937',
  },
  portraitPage: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    paddingTop: 40,
    paddingBottom: 50,
    paddingHorizontal: 50,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: '#1f2937',
  },
  // Cover page
  coverPage: {
    paddingTop: 50,
    paddingBottom: 50,
    justifyContent: 'flex-start',
  },
  coverHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: BRAND_BLUE,
    paddingBottom: 14,
    marginBottom: 0,
  },
  coverLogoPlaceholder: {
    width: 46,
    height: 46,
    marginRight: 14,
    backgroundColor: BRAND_BLUE,
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  coverLogoText: {
    color: '#ffffff',
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
  },
  coverBrandName: {
    fontSize: 16,
    fontFamily: 'Helvetica-Bold',
    color: BRAND_BLUE,
  },
  coverContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 80,
  },
  coverTitle: {
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
    color: '#0f172a',
    marginBottom: 14,
    textAlign: 'center',
    maxWidth: 500,
  },
  coverSubtitle: {
    fontSize: 13,
    color: '#374151',
    marginBottom: 6,
    textAlign: 'center',
  },
  coverMeta: {
    fontSize: 10,
    color: '#6b7280',
    marginTop: 20,
    textAlign: 'center',
  },
  coverFooter: {
    position: 'absolute',
    bottom: 50,
    left: 30,
    right: 30,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    paddingTop: 10,
  },
  coverFooterText: {
    fontSize: 7,
    color: '#9ca3af',
    textAlign: 'center',
  },
  // Page header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: '#d1d5db',
    paddingBottom: 8,
    marginBottom: 16,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoSmall: {
    width: 24,
    height: 24,
    marginRight: 8,
    backgroundColor: BRAND_BLUE,
    borderRadius: 3,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoSmallText: {
    color: '#ffffff',
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
  },
  headerTitle: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: '#374151',
  },
  headerMeta: {
    fontSize: 7,
    color: '#6b7280',
    marginTop: 1,
  },
  pageNumber: {
    position: 'absolute',
    bottom: 16,
    right: 30,
    fontSize: 7,
    color: '#9ca3af',
  },
  // Report content pages (portrait)
  reportSectionTitle: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    color: ACCENT_BLUE,
    marginBottom: 14,
    marginTop: 6,
  },
  reportBody: {
    fontSize: 10,
    color: '#374151',
    lineHeight: 1.6,
    marginBottom: 8,
  },
  reportBodyBold: {
    fontFamily: 'Helvetica-Bold',
  },
  reportBulletRow: {
    flexDirection: 'row',
    marginBottom: 3,
    paddingLeft: 10,
  },
  reportBulletChar: {
    width: 12,
    fontSize: 10,
    color: '#374151',
  },
  reportBulletText: {
    flex: 1,
    fontSize: 10,
    color: '#374151',
    lineHeight: 1.6,
  },
  reportActionTitle: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: ACCENT_BLUE,
    marginTop: 14,
    marginBottom: 6,
  },
  reportFieldRow: {
    flexDirection: 'row',
    marginBottom: 2,
    paddingLeft: 4,
  },
  reportFieldLabel: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: '#374151',
  },
  reportFieldValue: {
    fontSize: 10,
    color: '#374151',
  },
  reportSubHeading: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: '#374151',
    marginTop: 6,
    marginBottom: 3,
  },
  reportActionBlock: {
    marginBottom: 4,
  },
  reportDivider: {
    borderBottomWidth: 0.5,
    borderBottomColor: '#d1d5db',
    marginVertical: 8,
  },
  // Calendar table
  calendarTitle: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: '#0f172a',
    marginBottom: 8,
  },
  tableContainer: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 3,
  },
  tableRow: {
    flexDirection: 'row',
    minHeight: 28,
  },
  tableRowAlt: {
    backgroundColor: '#f9fafb',
  },
  rowLabel: {
    width: 140,
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: '#d1d5db',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    justifyContent: 'center',
  },
  rowLabelText: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: '#374151',
  },
  rowLabelPerson: {
    fontSize: 5.5,
    color: BRAND_BLUE,
    marginTop: 2,
    backgroundColor: '#e8edf6',
    borderRadius: 2,
    paddingHorizontal: 3,
    paddingVertical: 1.5,
  },
  rowLabelExtraTitle: {
    fontSize: 5,
    fontFamily: 'Helvetica-Bold',
    color: '#374151',
    marginTop: 2,
  },
  rowLabelExtraValue: {
    fontSize: 5,
    color: '#374151',
    lineHeight: 1.3,
  },
  monthHeaderCell: {
    flex: 1,
    padding: 3,
    borderRightWidth: 1,
    borderRightColor: '#d1d5db',
    borderBottomWidth: 1,
    borderBottomColor: '#d1d5db',
    backgroundColor: HEADER_BG,
    justifyContent: 'center',
    alignItems: 'center',
  },
  monthHeaderText: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
  },
  monthCell: {
    flex: 1,
    padding: 2,
    borderRightWidth: 1,
    borderRightColor: '#e5e7eb',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 28,
  },
  eventBadge: {
    paddingHorizontal: 3,
    paddingVertical: 2,
    borderRadius: 3,
    marginVertical: 1,
  },
  eventBadgeText: {
    fontSize: 6,
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
    textAlign: 'center',
  },
  eventDateText: {
    fontSize: 5.5,
    color: '#ffffff',
    textAlign: 'center',
    marginTop: 1,
  },
  continuousBand: {
    flex: 1,
    backgroundColor: '#7c3aed',
    borderRadius: 8,
    paddingVertical: 4,
    paddingHorizontal: 6,
    marginHorizontal: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  continuousBandRow: {
    flexDirection: 'row',
    flex: 1,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    alignItems: 'center',
    padding: 2,
  },
  continuousBandText: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
    textAlign: 'center',
  },
  // Person summary section
  sectionTitle: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    color: '#0f172a',
    marginBottom: 6,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: BRAND_BLUE,
  },
  personBlock: {
    marginBottom: 12,
    paddingLeft: 8,
    borderLeftWidth: 3,
    borderLeftColor: BRAND_BLUE,
  },
  personName: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: '#1f2937',
    marginBottom: 4,
  },
  responsibilityRow: {
    flexDirection: 'row',
    marginBottom: 3,
    paddingLeft: 6,
  },
  responsibilityBullet: {
    width: 6,
    fontSize: 9,
    color: BRAND_BLUE,
    marginRight: 4,
  },
  responsibilityText: {
    flex: 1,
    fontSize: 8.5,
    color: '#374151',
    lineHeight: 1.4,
  },
  responsibilityBold: {
    fontFamily: 'Helvetica-Bold',
  },
  noAssignments: {
    fontSize: 10,
    color: '#9ca3af',
    fontStyle: 'italic',
    paddingVertical: 16,
    textAlign: 'center',
  },
});

function labelFromPositionHuman(startMonth: number, position: number): string {
  const monthIndexFromStart = Math.floor(position);
  const fraction = position - monthIndexFromStart;
  const actualMonth = ((startMonth - 1 + monthIndexFromStart) % 12);
  const monthName = MONTHS_LOWER[actualMonth];
  if (fraction < 0.33) { return `début ${monthName}`; }
  if (fraction > 0.66) { return `fin ${monthName}`; }
  return `mi-${monthName}`;
}

function formatExactDate(date: { day: number; month: number }): string {
  return `${date.day} ${MOIS_COURTS[date.month - 1]}`;
}

function getMonthFromPosition(startMonth: number, position: number): number {
  const monthIndexFromStart = Math.floor(position);
  return ((startMonth - 1 + monthIndexFromStart) % 12) + 1;
}

const LogoPlaceholder: React.FC<{ size: 'small' | 'large' }> = ({ size }) => (
  <View style={size === 'large' ? styles.coverLogoPlaceholder : styles.logoSmall}>
    <Text style={size === 'large' ? styles.coverLogoText : styles.logoSmallText}>DJ</Text>
  </View>
);

const PageHeader: React.FC<{ companyName: string; year: number }> = ({ companyName, year }) => (
  <View style={styles.header} fixed>
    <View style={styles.headerLeft}>
      <LogoPlaceholder size="small" />
      <View>
        <Text style={styles.headerTitle}>Calendrier des Actions sur 12 Mois - {year}</Text>
        {companyName ? <Text style={styles.headerMeta}>{companyName}</Text> : null}
      </View>
    </View>
  </View>
);

interface EventInMonth {
  label: string;
  theme?: string;
  exactDate?: { day: number; month: number };
  color: string;
  optional?: boolean;
}

function buildCalendarData(props: PlanningPdfProps | BlankPlanningPdfProps, options?: { showAllEvents?: boolean }): { visibleRows: RowConfig[]; monthHeaders: string[]; grid: Record<string, EventInMonth[][]> } {
  const { startMonth, eventPositions, eventActivation, rowVisibility } = props;
  const exactDates = 'exactDates' in props ? props.exactDates : {};
  const showAllEvents = options?.showAllEvents ?? false;

  const monthHeaders = Array.from({ length: 12 }, (_, i) => {
    const monthIndex = (startMonth + i - 1) % 12;
    return MOIS_COURTS[monthIndex];
  });

  const defaultVisibility: Record<string, boolean> = {
    newsletter: true,
    'referent-mails': true,
    'sci-mails': true,
    boitage: true,
    dpe: true,
    'ag-communication': true,
    'property-communication': true,
    'follow-up-meetings': true,
  };
  const mergedVisibility = { ...defaultVisibility, ...rowVisibility };

  const visibleRows = ROW_CONFIGS.filter((row) => {
    if (showAllEvents && (row.eventType === 'referent' || row.eventType === 'sci-referent')) {
      return true;
    }
    if (mergedVisibility[row.key] === false) {return false;}

    if (row.eventType && row.eventType !== 'newsletter') {
      const activationMap = eventActivation[row.eventType as keyof DashboardEventActivation];
      if (activationMap && !Object.values(activationMap).some(Boolean)) {return false;}
    }
    return true;
  });

  const grid: Record<string, EventInMonth[][]> = {};

  for (const row of visibleRows) {
    const cells: EventInMonth[][] = Array.from({ length: 12 }, () => []);

    if (row.continuous) {
      grid[row.key] = cells;
      continue;
    }

    if (row.eventType === 'newsletter' && eventPositions.newsletter) {
      for (const [eventName, eventData] of Object.entries(eventPositions.newsletter)) {
        const monthNum = getMonthFromPosition(startMonth, eventData.position);
        const colIndex = ((monthNum - startMonth + 12) % 12);
        const dateKey = `newsletter-${eventName}`;
        cells[colIndex].push({
          label: eventName,
          theme: eventData.theme,
          exactDate: exactDates[dateKey],
          color: TYPE_COLORS.newsletter,
        });
      }
    } else if (row.eventType) {
      const positionsMap = eventPositions[row.eventType as keyof Omit<DashboardEventPositions, 'newsletter'>] as Record<string, number> | undefined;
      const activationMap = eventActivation[row.eventType as keyof DashboardEventActivation];

      if (!positionsMap) {
        grid[row.key] = cells;
        continue;
      }

      for (const [eventName, position] of Object.entries(positionsMap)) {
        const skipInactive = showAllEvents && (row.eventType === 'referent' || row.eventType === 'sci-referent')
          ? false
          : activationMap && activationMap[eventName] === false;
        if (skipInactive) {continue;}
        const monthNum = getMonthFromPosition(startMonth, position);
        const colIndex = ((monthNum - startMonth + 12) % 12);
        const dateKey = `${row.eventType}-${eventName}`;
        const isOptional = showAllEvents && activationMap && activationMap[eventName] === false;
        cells[colIndex].push({
          label: eventName,
          exactDate: exactDates[dateKey],
          color: TYPE_COLORS[row.eventType] || BRAND_BLUE,
          optional: isOptional,
        });
      }
    }

    grid[row.key] = cells;
  }

  return { visibleRows, monthHeaders, grid };
}

interface PersonSummary {
  userId: number;
  name: string;
  responsibilities: { rowLabel: string; events: { name: string; period: string; exactDate?: string }[] }[];
}

function buildPersonSummaries(props: PlanningPdfProps): PersonSummary[] {
  const { personAssignments, companyUsers, eventPositions, eventActivation, exactDates, startMonth } = props;

  const summaryMap = new Map<number, PersonSummary>();

  for (const [rowKey, userId] of Object.entries(personAssignments)) {
    if (!userId) {continue;}

    const user = companyUsers.find((u) => u.id === userId);
    if (!user) {continue;}

    if (!summaryMap.has(userId)) {
      summaryMap.set(userId, {
        userId,
        name: `${user.firstname} ${user.lastname ? user.lastname.toUpperCase() : ''}`,
        responsibilities: [],
      });
    }

    const rowConfig = ROW_CONFIGS.find((r) => r.key === rowKey);
    if (!rowConfig) {continue;}

    const events: { name: string; period: string; exactDate?: string }[] = [];

    if (rowConfig.continuous) {
      events.push({ name: rowConfig.continuousLabel || 'Action continue', period: 'Toute l\'année' });
    } else if (rowConfig.eventType === 'newsletter' && eventPositions.newsletter) {
      for (const [eventName, eventData] of Object.entries(eventPositions.newsletter)) {
        const period = labelFromPositionHuman(startMonth, eventData.position);
        const dateKey = `newsletter-${eventName}`;
        const exact = exactDates[dateKey];
        events.push({
          name: `${eventName} (${eventData.theme})`,
          period,
          exactDate: exact ? formatExactDate(exact) : undefined,
        });
      }
    } else if (rowConfig.eventType) {
      const positionsMap = eventPositions[rowConfig.eventType as keyof Omit<DashboardEventPositions, 'newsletter'>] as Record<string, number> | undefined;
      const activationMap = eventActivation[rowConfig.eventType as keyof DashboardEventActivation];

      if (!positionsMap) {
        continue;
      }

      for (const [eventName, position] of Object.entries(positionsMap)) {
        if (activationMap && activationMap[eventName] === false) {continue;}
        const period = labelFromPositionHuman(startMonth, position);
        const dateKey = `${rowConfig.eventType}-${eventName}`;
        const exact = exactDates[dateKey];
        events.push({
          name: eventName,
          period,
          exactDate: exact ? formatExactDate(exact) : undefined,
        });
      }
    }

    if (events.length > 0) {
      summaryMap.get(userId)!.responsibilities.push({
        rowLabel: rowConfig.label,
        events,
      });
    }
  }

  const followupPositions = eventPositions.followup;
  if (followupPositions && Object.keys(followupPositions).length > 0) {
    const followupEvents: { name: string; period: string; exactDate?: string }[] = [];
    for (const [eventName, position] of Object.entries(followupPositions)) {
      const period = labelFromPositionHuman(startMonth, position);
      const dateKey = `followup-${eventName}`;
      const exact = exactDates[dateKey];
      followupEvents.push({
        name: eventName,
        period,
        exactDate: exact ? formatExactDate(exact) : undefined,
      });
    }

    if (followupEvents.length > 0) {
      const followupRow = ROW_CONFIGS.find((r) => r.key === 'follow-up-meetings');
      const attendeeIds = new Set<number>();
      for (const userId of Object.values(personAssignments)) {
        if (userId) {attendeeIds.add(userId);}
      }
      const legalRep = companyUsers.find((u) =>
        u.fonctions?.some((f) => f.type === 2),
      );
      if (legalRep) {attendeeIds.add(legalRep.id);}

      for (const uid of attendeeIds) {
        const user = companyUsers.find((u) => u.id === uid);
        if (!user) {continue;}
        if (!summaryMap.has(uid)) {
          summaryMap.set(uid, {
            userId: uid,
            name: `${user.firstname} ${user.lastname ? user.lastname.toUpperCase() : ''}`,
            responsibilities: [],
          });
        }
        summaryMap.get(uid)!.responsibilities.push({
          rowLabel: followupRow?.label || 'Points d\u2019avancement',
          events: followupEvents,
        });
      }
    }
  }

  return [...summaryMap.values()].filter((s) => s.responsibilities.length > 0);
}

interface ActionSection {
  title: string;
  rowKey: string;
  miseEnOeuvre: string;
  methodologie: string;
}

const ACTION_SECTIONS: ActionSection[] = [
  {
    title: '1. Envoi des newsletters trimestrielles',
    rowKey: 'newsletter',
    miseEnOeuvre: 'Cette action consiste à adresser aux copropriétaires une newsletter d\u2019information régulière relative à la vie de la copropriété, aux évolutions réglementaires, aux enjeux énergétiques ainsi qu\u2019aux informations patrimoniales utiles. Elle s\u2019inscrit dans une démarche d\u2019information et de transparence, sans vocation commerciale directe. Sur la quatrième de couverture de chaque newsletter est également présenté le référent mutation-location afin d\u2019informer régulièrement les copropriétaires de l\u2019existence de ce service et de les inviter, s\u2019ils le souhaitent, à prendre contact avec lui.',
    methodologie: 'Les contenus sont extraits du module Croissance puis transmis au prestataire chargé des campagnes d\u2019e-mailing. Celui-ci assure l\u2019envoi et le suivi technique des communications. Les éventuelles demandes de désinscription ou refus sont communiquées au syndic afin d\u2019être intégrées dans la base interne et respectées lors des campagnes suivantes conformément au RGPD.',
  },
  {
    title: '2. Présentation du référent mutation / location',
    rowKey: 'referent-mails',
    miseEnOeuvre: 'Cette communication vise à informer les copropriétaires de l\u2019existence du référent mutation-location, de son rôle d\u2019accompagnement informatif et des services pouvant être proposés dans le respect du cadre réglementaire applicable aux syndics.',
    methodologie: 'Les contenus sont extraits du module puis transmis au prestataire d\u2019envoi des e-mails. Celui-ci assure la diffusion et communique les éventuels refus afin qu\u2019ils soient intégrés dans la base interne conformément aux obligations RGPD.',
  },
  {
    title: '3. Communication ciblée auprès des SCI (transaction / gestion)',
    rowKey: 'sci-mails',
    miseEnOeuvre: 'Communication spécifique auprès des propriétaires personnes morales (SCI notamment) afin de leur transmettre des informations adaptées à leurs enjeux patrimoniaux et locatifs. Cette action permet également de promouvoir l\u2019activité de gestion locative auprès de ces acteurs dans un cadre réglementaire conforme.',
    methodologie: 'Filtrer les copropriétaires qualifiés personnes morales dans le module, préparer la communication ciblée, enregistrer l\u2019action réalisée et suivre les retours.',
  },
  {
    title: '4. Actions de boîtage auprès des publics à fort potentiel',
    rowKey: 'boitage',
    miseEnOeuvre: 'Diffusion ciblée de supports de communication auprès de copropriétaires ou copropriétés identifiés comme présentant un potentiel en matière de transaction immobilière. Cette action commerciale est réalisée dans le respect du RGPD et des modèles validés.',
    methodologie: 'Identifier les cibles, générer les supports depuis le module, organiser la diffusion et enregistrer l\u2019action afin d\u2019assurer le suivi.',
  },
  {
    title: '5. Suivi et traitement des DPE',
    rowKey: 'dpe',
    miseEnOeuvre: 'Suivi hebdomadaire des DPE publiés par l\u2019ADEME tous les mercredis sur les immeubles gérés par votre syndic. Le référent mutation-location, après avoir qualifié les copropriétaires concernés, doit leur envoyer le mail RGPD compatible avant de prendre contact avec eux. IMPORTANT : Cette action de traitement des DPE hebdomadaire ne peut être faite que si la première newsletter DPE a été envoyée. Le mail envoyé au copropriétaire doit être obligatoirement signé par le référent mutation-location.',
    methodologie: 'Consultation hebdomadaire des DPE, qualification des copropriétaires, déclenchement des communications associées et enregistrement des actions dans le module.',
  },
  {
    title: '6. Communication lors des assemblées générales',
    rowKey: 'ag-communication',
    miseEnOeuvre: 'Des supports de communication sont mis à disposition sur les tables lors des assemblées générales afin de présenter le référent mutation-location et d\u2019engager des échanges avec les copropriétaires concernés. Pour les assemblées importantes, le référent peut être présent afin de faciliter les échanges dans un cadre informatif.',
    methodologie: 'Télécharger les supports AG depuis le module, personnaliser si nécessaire, enregistrer l\u2019événement et la participation éventuelle du référent.',
  },
  {
    title: '7. Communication sur les biens à vendre ou à louer',
    rowKey: 'property-communication',
    miseEnOeuvre: 'Lorsqu\u2019un mandat de vente est confié dans la copropriété, une phase « hors market » peut être privilégiée : boîtage interne auprès des copropriétaires puis prise de contact avec les SCI présentes afin d\u2019identifier d\u2019éventuelles opportunités patrimoniales avant diffusion large.',
    methodologie: 'Créer l\u2019opportunité dans le module dès l\u2019entrée du mandat, générer les supports internes, identifier les SCI concernées, tracer les actions puis déclencher si nécessaire la commercialisation élargie.',
  },
  {
    title: '8. Points d\u2019avancement avec Déclarations Juridiques',
    rowKey: 'follow-up-meetings',
    miseEnOeuvre: 'Deux réunions de suivi planifiées avec l\u2019équipe de Déclarations Juridiques permettent d\u2019évaluer l\u2019avancement de la stratégie, de mesurer les résultats obtenus et d\u2019ajuster le plan d\u2019actions si nécessaire. Ces réunions réunissent l\u2019ensemble des responsables désignés ainsi que le représentant légal de l\u2019entreprise.',
    methodologie: 'Les invitations calendrier (ICS) sont adressées automatiquement à tous les responsables identifiés dans le planning ainsi qu\u2019au représentant légal de l\u2019entreprise. Un ordre du jour est préparé en amont par Déclarations Juridiques, incluant le bilan des actions réalisées, les indicateurs de performance et les éventuels ajustements à apporter.',
  },
];

function getResponsibleForRow(rowKey: string, personAssignments: DashboardPersonAssignments, companyUsers: User[]): string {
  if (rowKey === 'follow-up-meetings') {
    const attendeeIds = new Set<number>();
    for (const uid of Object.values(personAssignments)) {
      if (uid) {attendeeIds.add(uid);}
    }
    const legalRep = companyUsers.find((u) =>
      u.fonctions?.some((f) => f.type === 2),
    );
    if (legalRep) {attendeeIds.add(legalRep.id);}
    const names = [...attendeeIds]
      .map((id) => companyUsers.find((u) => u.id === id))
      .filter(Boolean)
      .map((u) => `${u!.firstname} ${u!.lastname ? u!.lastname.toUpperCase() : ''}`.trim());
    return names.length > 0 ? names.join(', ') : 'Non attribué';
  }
  const userId = personAssignments[rowKey];
  if (!userId) {return 'Non attribué';}
  const user = companyUsers.find((u) => u.id === userId);
  if (!user) {return 'Non attribué';}
  return `${user.firstname} ${user.lastname ? user.lastname.toUpperCase() : ''}`.trim();
}

function getDatesForRow(rowKey: string, props: PlanningPdfProps): string {
  const { startMonth, eventPositions, eventActivation, exactDates } = props;
  const rowConfig = ROW_CONFIGS.find((r) => r.key === rowKey);
  if (!rowConfig) {return '';}

  if (rowConfig.continuous) {
    if (rowKey === 'dpe') {return 'chaque mercredi (à partir de 14 h)';}
    if (rowKey === 'ag-communication') {return 'selon calendrier des AG';}
    return 'selon entrée de mandat';
  }

  const dates: string[] = [];

  if (rowConfig.eventType === 'newsletter' && eventPositions.newsletter) {
    for (const [eventName, eventData] of Object.entries(eventPositions.newsletter)) {
      const dateKey = `newsletter-${eventName}`;
      const exact = exactDates[dateKey];
      dates.push(exact ? `${exact.day} ${MONTHS_LOWER[exact.month - 1]}` : labelFromPositionHuman(startMonth, eventData.position));
    }
  } else if (rowConfig.eventType) {
    const positionsMap = eventPositions[rowConfig.eventType as keyof Omit<DashboardEventPositions, 'newsletter'>] as Record<string, number> | undefined;
    const activationMap = eventActivation[rowConfig.eventType as keyof DashboardEventActivation];

    if (positionsMap) {
      for (const [eventName, position] of Object.entries(positionsMap)) {
        if (activationMap && activationMap[eventName] === false) {continue;}
        const dateKey = `${rowConfig.eventType}-${eventName}`;
        const exact = exactDates[dateKey];
        dates.push(exact ? `${exact.day} ${MONTHS_LOWER[exact.month - 1]}` : labelFromPositionHuman(startMonth, position));
      }
    }
  }

  return dates.length > 0 ? dates.join(' – ') : 'À planifier';
}

const PortraitPageHeader: React.FC<{ companyName: string; year: number }> = ({ companyName, year }) => (
  <View style={[styles.header, { marginBottom: 10 }]} fixed>
    <View style={styles.headerLeft}>
      <LogoPlaceholder size="small" />
      <View>
        <Text style={styles.headerTitle}>Plan stratégique d{'\u2019'}actions - {year}</Text>
        {companyName ? <Text style={styles.headerMeta}>{companyName}</Text> : null}
      </View>
    </View>
  </View>
);

interface ReportActionExtraField {
  label: string;
  value: string;
}

const ReportActionBlock: React.FC<{
  section: ActionSection;
  responsible: string;
  dates: string;
  extraFields?: ReportActionExtraField[];
}> = ({ section, responsible, dates, extraFields }) => (
  <View style={styles.reportActionBlock} wrap={false}>
    <Text style={styles.reportActionTitle}>{section.title}</Text>
    <View style={styles.reportFieldRow}>
      <Text style={styles.reportFieldLabel}>Responsable : </Text>
      <Text style={styles.reportFieldValue}>{responsible}</Text>
    </View>
    <View style={styles.reportFieldRow}>
      <Text style={styles.reportFieldLabel}>Dates prévues : </Text>
      <Text style={styles.reportFieldValue}>{dates}</Text>
    </View>
    {extraFields?.map((field) => (
      <View key={field.label} style={styles.reportFieldRow}>
        <Text style={styles.reportFieldLabel}>{field.label} : </Text>
        <Text style={styles.reportFieldValue}>{field.value}</Text>
      </View>
    ))}
    <Text style={styles.reportSubHeading}>Mise en œuvre</Text>
    <Text style={styles.reportBody}>{section.miseEnOeuvre}</Text>
    <Text style={styles.reportSubHeading}>Méthodologie (module Croissance)</Text>
    <Text style={styles.reportBody}>{section.methodologie}</Text>
    <View style={styles.reportDivider} />
  </View>
);

const PlanningPdfDocument: React.FC<PlanningPdfProps> = (props) => {
  const {
    companyName,
    year,
    personAssignments,
    companyUsers,
    referentSignataires,
    dpeQualificationManagers,
    agDocumentManagers,
  } = props;
  const generatedAt = new Date().toLocaleString('fr-FR');
  const { visibleRows, monthHeaders, grid } = buildCalendarData(props);
  const personSummaries = buildPersonSummaries(props);

  const renderRowLabelExtras = (rowKey: string) => {
    if (rowKey === 'dpe') {
      return (
        <>
          <Text style={styles.rowLabelExtraTitle}>
            {pluralizeFr(SIGNATAIRES_LABEL.singular, SIGNATAIRES_LABEL.plural, referentSignataires)} :
          </Text>
          <Text style={styles.rowLabelExtraValue}>{formatUserList(referentSignataires)}</Text>
          <Text style={styles.rowLabelExtraTitle}>
            {pluralizeFr(CHARGE_QUALIF_LABEL.singular, CHARGE_QUALIF_LABEL.plural, dpeQualificationManagers)} :
          </Text>
          <Text style={styles.rowLabelExtraValue}>{formatUserList(dpeQualificationManagers)}</Text>
        </>
      );
    }
    if (rowKey === 'ag-communication') {
      return (
        <>
          <Text style={styles.rowLabelExtraTitle}>
            {pluralizeFr(RESPONSABLE_AG_LABEL.singular, RESPONSABLE_AG_LABEL.plural, agDocumentManagers)} :
          </Text>
          <Text style={styles.rowLabelExtraValue}>{formatUserList(agDocumentManagers)}</Text>
        </>
      );
    }
    return null;
  };

  return (
    <Document title={`Calendrier des Actions sur 12 Mois - ${year}`} author="DÉCLARATIONS JURIDIQUES" subject="Planning stratégique annuel">
      {/* Cover Page (portrait) */}
      <Page size={A4_PORTRAIT} style={[styles.portraitPage, styles.coverPage]}>
        <View style={styles.coverHeader}>
          <LogoPlaceholder size="large" />
          <Text style={styles.coverBrandName}>DÉCLARATIONS JURIDIQUES</Text>
        </View>
        <View style={styles.coverContent}>
          <Text style={styles.coverTitle}>Calendrier des Actions sur 12 Mois</Text>
          {companyName ? <Text style={styles.coverSubtitle}>{companyName}</Text> : null}
          <Text style={styles.coverSubtitle}>Année {year}</Text>
          <Text style={styles.coverMeta}>{`Généré le : ${generatedAt}`}</Text>
        </View>
        <View style={styles.coverFooter}>
          <Text style={styles.coverFooterText}>
            Document généré automatiquement - Planning stratégique des actions commerciales
          </Text>
        </View>
      </Page>

      {/* Calendar Page (landscape) */}
      <Page size={A4_LANDSCAPE} style={styles.page} wrap>
        <PageHeader companyName={companyName} year={year} />

        <Text style={styles.calendarTitle}>Planning des Actions</Text>

        <View style={styles.tableContainer}>
          {/* Month headers */}
          <View style={styles.tableRow}>
            <View style={[styles.rowLabel, { backgroundColor: HEADER_BG, borderBottomColor: '#d1d5db' }]}>
              <Text style={[styles.rowLabelText, { color: '#ffffff' }]}>Action</Text>
            </View>
            {monthHeaders.map((mh, i) => (
              <View key={i} style={[styles.monthHeaderCell, i === 11 ? { borderRightWidth: 0 } : {}]}>
                <Text style={styles.monthHeaderText}>{mh}</Text>
              </View>
            ))}
          </View>

          {/* Data rows */}
          {visibleRows.map((row, rowIdx) => {
            const isAlt = rowIdx % 2 === 1;

            if (row.continuous) {
              const responsible = getResponsibleForRow(row.key, personAssignments, companyUsers);
              return (
                <View key={row.key} style={[styles.tableRow, isAlt ? styles.tableRowAlt : {}]}>
                  <View style={styles.rowLabel}>
                    <Text style={styles.rowLabelText}>{row.label}</Text>
                    <Text style={styles.rowLabelPerson}>{responsible}</Text>
                    {renderRowLabelExtras(row.key)}
                  </View>
                  <View style={styles.continuousBandRow}>
                    <View style={styles.continuousBand}>
                      <Text style={styles.continuousBandText}>{row.continuousLabel}</Text>
                    </View>
                  </View>
                </View>
              );
            }

            const cells = grid[row.key] || Array.from({ length: 12 }, () => []);
            const responsible = getResponsibleForRow(row.key, personAssignments, companyUsers);

            return (
              <View key={row.key} style={[styles.tableRow, isAlt ? styles.tableRowAlt : {}]} wrap={false}>
                <View style={styles.rowLabel}>
                  <Text style={styles.rowLabelText}>{row.label}</Text>
                  <Text style={styles.rowLabelPerson}>{responsible}</Text>
                </View>
                {cells.map((eventsInCell, colIdx) => (
                  <View key={colIdx} style={[styles.monthCell, colIdx === 11 ? { borderRightWidth: 0 } : {}]}>
                    {eventsInCell.map((evt, evtIdx) => (
                      <View key={evtIdx} style={[styles.eventBadge, { backgroundColor: evt.color }]}>
                        <Text style={styles.eventBadgeText}>
                          {evt.theme || evt.label}
                        </Text>
                        {evt.exactDate && (
                          <Text style={styles.eventDateText}>{formatExactDate(evt.exactDate)}</Text>
                        )}
                      </View>
                    ))}
                  </View>
                ))}
              </View>
            );
          })}
        </View>

        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>

      {/* Report: Introduction (portrait) */}
      <Page size={A4_PORTRAIT} style={styles.portraitPage} wrap>
        <PortraitPageHeader companyName={companyName} year={year} />

        <Text style={styles.reportSectionTitle}>Module Croissance – Plan stratégique d{'\u2019'}actions</Text>

        <Text style={styles.reportBody}>
          <Text>Dans le cadre du module </Text>
          <Text style={styles.reportBodyBold}>Croissance</Text>
          <Text>, une stratégie d{'\u2019'}actions est définie conjointement avec le syndic afin de structurer dans la durée la communication, le suivi des opportunités immobilières et l{'\u2019'}accompagnement des copropriétaires. Cette démarche vise à améliorer l{'\u2019'}information des copropriétaires, à renforcer la valorisation patrimoniale de la copropriété et à organiser les actions de communication dans un cadre clair, suivi et conforme aux obligations réglementaires.</Text>
        </Text>

        <Text style={styles.reportBody}>
          <Text>Cette stratégie repose sur </Text>
          <Text style={styles.reportBodyBold}>huit actions complémentaires</Text>
          <Text>, chacune faisant l{'\u2019'}objet :</Text>
        </Text>

        <View style={styles.reportBulletRow}>
          <Text style={styles.reportBulletChar}>•</Text>
          <Text style={styles.reportBulletText}>d{'\u2019'}un responsable identifié,</Text>
        </View>
        <View style={styles.reportBulletRow}>
          <Text style={styles.reportBulletChar}>•</Text>
          <Text style={styles.reportBulletText}>d{'\u2019'}un calendrier prévisionnel,</Text>
        </View>
        <View style={styles.reportBulletRow}>
          <Text style={styles.reportBulletChar}>•</Text>
          <Text style={styles.reportBulletText}>d{'\u2019'}une méthodologie de mise en œuvre s{'\u2019'}appuyant sur le module Croissance.</Text>
        </View>

        <Text style={[styles.reportBody, { marginTop: 8 }]}>
          <Text>Afin d{'\u2019'}assurer une bonne coordination opérationnelle, </Text>
          <Text style={styles.reportBodyBold}>un courriel de rappel est adressé huit jours avant chaque action au responsable désigné</Text>
          <Text> afin de lui permettre d{'\u2019'}en organiser la préparation.</Text>
        </Text>

        <Text style={styles.reportBody}>
          <Text>Par ailleurs, </Text>
          <Text style={styles.reportBodyBold}>deux points d{'\u2019'}avancement annuels</Text>
          <Text> seront réalisés afin d{'\u2019'}évaluer la mise en œuvre de la stratégie, d{'\u2019'}en mesurer les effets et d{'\u2019'}ajuster si nécessaire les actions prévues.</Text>
        </Text>

        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>

      {/* Report: Plan d'actions (portrait) */}
      <Page size={A4_PORTRAIT} style={styles.portraitPage} wrap>
        <PortraitPageHeader companyName={companyName} year={year} />

        <Text style={styles.reportSectionTitle}>Plan d{'\u2019'}actions</Text>

        {ACTION_SECTIONS.map((section) => {
          const extraFields: ReportActionExtraField[] = [];
          if (section.rowKey === 'dpe') {
            extraFields.push({
              label: pluralizeFr(SIGNATAIRES_LABEL.singular, SIGNATAIRES_LABEL.plural, referentSignataires),
              value: formatUserList(referentSignataires),
            });
            extraFields.push({
              label: pluralizeFr(CHARGE_QUALIF_LABEL.singular, CHARGE_QUALIF_LABEL.plural, dpeQualificationManagers),
              value: formatUserList(dpeQualificationManagers),
            });
          } else if (section.rowKey === 'ag-communication') {
            extraFields.push({
              label: pluralizeFr(RESPONSABLE_AG_LABEL.singular, RESPONSABLE_AG_LABEL.plural, agDocumentManagers),
              value: formatUserList(agDocumentManagers),
            });
          }
          return (
            <ReportActionBlock
              key={section.rowKey}
              section={section}
              responsible={getResponsibleForRow(section.rowKey, personAssignments, companyUsers)}
              dates={getDatesForRow(section.rowKey, props)}
              extraFields={extraFields.length > 0 ? extraFields : undefined}
            />
          );
        })}

        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>

      {/* Report: Conclusion (portrait) */}
      <Page size={A4_PORTRAIT} style={styles.portraitPage} wrap>
        <PortraitPageHeader companyName={companyName} year={year} />

        <Text style={styles.reportSectionTitle}>Conclusion</Text>

        <Text style={styles.reportBody}>
          Les dispositifs présentés ont vocation à s{'\u2019'}inscrire dans la durée afin de structurer l{'\u2019'}actualisation des données, l{'\u2019'}information des copropriétaires et l{'\u2019'}approche patrimoniale globale au sein de la copropriété. Leur mise en œuvre est réalisée dans le respect strict de la réglementation relative à la protection des données personnelles (RGPD) et en veillant à éviter tout détournement de finalité dans l{'\u2019'}utilisation des informations collectées.
        </Text>

        <Text style={styles.reportBody}>
          L{'\u2019'}objectif de cette démarche globale est de promouvoir, de manière encadrée et conforme aux obligations réglementaires, les activités de transaction immobilière et de gestion locative du cabinet auprès de ses copropriétaires, tout en préservant un cadre juridique sécurisé et transparent.
        </Text>

        <Text style={styles.reportBody}>
          <Text>Dans ce contexte, le </Text>
          <Text style={styles.reportBodyBold}>référent mutation-location</Text>
          <Text> constitue un interlocuteur identifié, juridiquement sécurisé et adapté pour centraliser les échanges relatifs aux projets immobiliers des copropriétaires. Son intervention permet de maintenir une approche conforme aux exigences réglementaires et d{'\u2019'}éviter tout risque de confusion entre information institutionnelle et démarche commerciale directe.</Text>
        </Text>

        <Text style={styles.reportBody}>
          <Text>Cette stratégie fera l{'\u2019'}objet d{'\u2019'}un suivi régulier afin d{'\u2019'}en mesurer l{'\u2019'}efficacité. </Text>
          <Text style={styles.reportBodyBold}>Deux points d{'\u2019'}avancement annuels</Text>
          <Text> seront réalisés afin d{'\u2019'}évaluer la mise en œuvre de la stratégie, d{'\u2019'}en mesurer les effets et d{'\u2019'}ajuster si nécessaire les actions prévues.</Text>
        </Text>

        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>

      {/* Person Summary Page (portrait) */}
      <Page size={A4_PORTRAIT} style={styles.portraitPage} wrap>
        <PortraitPageHeader companyName={companyName} year={year} />

        <Text style={styles.sectionTitle}>Récapitulatif des responsabilités par personne</Text>

        {personSummaries.length === 0 ? (
          <Text style={styles.noAssignments}>Aucune attribution de responsable n'a été définie.</Text>
        ) : (
          personSummaries.map((person) => (
            <View key={person.userId} style={styles.personBlock} wrap={false}>
              <Text style={styles.personName}>{person.name}</Text>
              {person.responsibilities.map((resp, rIdx) => (
                <View key={rIdx}>
                  <View style={styles.responsibilityRow}>
                    <Text style={styles.responsibilityBullet}>•</Text>
                    <Text style={[styles.responsibilityText, styles.responsibilityBold]}>{resp.rowLabel}</Text>
                  </View>
                  {resp.events.map((evt, eIdx) => (
                    <View key={eIdx} style={[styles.responsibilityRow, { paddingLeft: 18 }]}>
                      <Text style={styles.responsibilityBullet}>-</Text>
                      <Text style={styles.responsibilityText}>
                        {evt.name} — Période : {evt.period}{evt.exactDate ? ` — Date exacte : ${evt.exactDate}` : ''}
                      </Text>
                    </View>
                  ))}
                </View>
              ))}
            </View>
          ))
        )}

        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>
    </Document>
  );
};

export interface BlankPlanningPdfProps {
  year: number;
  startMonth: number;
  eventPositions: DashboardEventPositions;
  eventActivation: DashboardEventActivation;
  rowVisibility: Record<string, boolean>;
}

const BLANK_LINE = '______________________________';
const BLANK_DATE = '____ / ____ / ________';

const blankStyles = StyleSheet.create({
  blankBadge: {
    borderWidth: 1,
    borderStyle: 'dashed',
    paddingHorizontal: 3,
    paddingVertical: 2,
    borderRadius: 3,
    marginVertical: 1,
  },
  blankDateText: {
    fontSize: 5,
    color: '#9ca3af',
    textAlign: 'center',
    marginTop: 1,
  },
  blankPersonLine: {
    fontSize: 5.5,
    color: '#9ca3af',
    marginTop: 2,
    borderBottomWidth: 0.5,
    borderBottomColor: '#9ca3af',
    borderStyle: 'dashed',
    paddingBottom: 1,
  },
  blankFieldLine: {
    fontSize: 10,
    color: '#9ca3af',
    borderBottomWidth: 0.5,
    borderBottomColor: '#9ca3af',
    borderStyle: 'dashed',
    paddingBottom: 2,
    flex: 1,
  },
  attributionTable: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 3,
    marginTop: 12,
  },
  attributionHeaderRow: {
    flexDirection: 'row',
    backgroundColor: HEADER_BG,
    minHeight: 28,
  },
  attributionHeaderCell: {
    padding: 6,
    justifyContent: 'center',
    borderRightWidth: 1,
    borderRightColor: '#d1d5db',
  },
  attributionHeaderText: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
  },
  attributionRow: {
    flexDirection: 'row',
    minHeight: 36,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  attributionCell: {
    padding: 6,
    justifyContent: 'center',
    borderRightWidth: 1,
    borderRightColor: '#e5e7eb',
  },
  attributionLabelText: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: '#374151',
  },
  attributionBlankText: {
    fontSize: 7,
    color: '#d1d5db',
  },
  coverBlankNotice: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: ACCENT_BLUE,
    marginTop: 10,
    textAlign: 'center',
    borderWidth: 1,
    borderColor: ACCENT_BLUE,
    borderRadius: 4,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  coverCompanyLine: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#9ca3af',
    borderStyle: 'dashed',
    paddingBottom: 4,
    width: 300,
    textAlign: 'center',
  },
});

interface BlankEventDate {
  label: string;
  period: string;
  optional?: boolean;
}

function getBlankEventDates(rowKey: string, props: BlankPlanningPdfProps, options?: { showAllEvents?: boolean }): { continuous: boolean; continuousLabel?: string; events: BlankEventDate[] } {
  const { startMonth, eventPositions, eventActivation } = props;
  const showAllEvents = options?.showAllEvents ?? false;
  const rowConfig = ROW_CONFIGS.find((r) => r.key === rowKey);
  if (!rowConfig) {return { continuous: false, events: [] };}

  if (rowConfig.continuous) {
    let label = 'selon entrée de mandat';
    if (rowKey === 'dpe') {label = 'chaque mercredi (à partir de 14 h)';}
    if (rowKey === 'ag-communication') {label = 'selon calendrier des AG';}
    return { continuous: true, continuousLabel: label, events: [] };
  }

  const events: BlankEventDate[] = [];
  const forceShow = showAllEvents && (rowConfig.eventType === 'referent' || rowConfig.eventType === 'sci-referent');

  if (rowConfig.eventType === 'newsletter' && eventPositions.newsletter) {
    for (const [eventName, eventData] of Object.entries(eventPositions.newsletter)) {
      events.push({ label: eventName, period: labelFromPositionHuman(startMonth, eventData.position) });
    }
  } else if (rowConfig.eventType) {
    const positionsMap = eventPositions[rowConfig.eventType as keyof Omit<DashboardEventPositions, 'newsletter'>] as Record<string, number> | undefined;
    const activationMap = eventActivation[rowConfig.eventType as keyof DashboardEventActivation];

    if (positionsMap) {
      for (const [eventName, position] of Object.entries(positionsMap)) {
        if (!forceShow && activationMap && activationMap[eventName] === false) {continue;}
        const isOptional = forceShow && activationMap && activationMap[eventName] === false;
        events.push({ label: eventName, period: labelFromPositionHuman(startMonth, position), optional: isOptional });
      }
    }
  }

  return { continuous: false, events };
}

const BlankReportActionBlock: React.FC<{ section: ActionSection; actionIndex: number; eventDates: ReturnType<typeof getBlankEventDates> }> = ({ section, actionIndex, eventDates }) => {
  const actionTitle = section.title.replace(/^\d+\.\s*/, '');

  return (
    <View style={styles.reportActionBlock} wrap={false}>
      <Text style={styles.reportActionTitle}>Action {actionIndex} : {actionTitle}</Text>
      <View style={[styles.reportFieldRow, { alignItems: 'flex-end' }]}>
        <Text style={styles.reportFieldLabel}>Responsable : </Text>
        <Text style={blankStyles.blankFieldLine}>{BLANK_LINE}</Text>
      </View>

      {eventDates.continuous ? (
        <View style={[styles.reportFieldRow, { marginTop: 4 }]}>
          <Text style={styles.reportFieldLabel}>P{'\u00E9'}riode : </Text>
          <Text style={styles.reportFieldValue}>{eventDates.continuousLabel}</Text>
        </View>
      ) : eventDates.events.length > 0 ? (
        eventDates.events.map((evt, idx) => {
          const hasMultiple = eventDates.events.length > 1;
          const prefix = hasMultiple ? `${idx + 1}. ` : '';
          const optionalSuffix = evt.optional ? ' (optionnel)' : '';
          return (
            <View key={idx} style={{ marginTop: 4, paddingLeft: 4, opacity: evt.optional ? 0.6 : 1 }}>
              <View style={styles.reportFieldRow}>
                <Text style={styles.reportFieldLabel}>{prefix}{evt.label}{optionalSuffix} {'\u2014'} P{'\u00E9'}riode sugg{'\u00E9'}r{'\u00E9'}e : </Text>
                <Text style={styles.reportFieldValue}>{evt.period}</Text>
              </View>
              <View style={[styles.reportFieldRow, { alignItems: 'flex-end', marginTop: 2, paddingLeft: hasMultiple ? 10 : 0 }]}>
                <Text style={styles.reportFieldLabel}>Date exacte retenue : </Text>
                <Text style={blankStyles.blankFieldLine}>{BLANK_DATE}</Text>
              </View>
            </View>
          );
        })
      ) : (
        <View style={{ marginTop: 4, paddingLeft: 4 }}>
          <View style={[styles.reportFieldRow, { alignItems: 'flex-end' }]}>
            <Text style={styles.reportFieldLabel}>P{'\u00E9'}riode sugg{'\u00E9'}r{'\u00E9'}e : </Text>
            <Text style={styles.reportFieldValue}>{'\u00C0'} planifier</Text>
          </View>
          <View style={[styles.reportFieldRow, { alignItems: 'flex-end', marginTop: 2 }]}>
            <Text style={styles.reportFieldLabel}>Date exacte retenue : </Text>
            <Text style={blankStyles.blankFieldLine}>{BLANK_DATE}</Text>
          </View>
        </View>
      )}

      <Text style={styles.reportSubHeading}>Mise en {'\u0153'}uvre</Text>
      <Text style={styles.reportBody}>{section.miseEnOeuvre}</Text>
      <View style={styles.reportDivider} />
    </View>
  );
};

const BlankPlanningPdfDocument: React.FC<BlankPlanningPdfProps> = (props) => {
  const { year } = props;
  const generatedAt = new Date().toLocaleString('fr-FR');
  const { visibleRows, monthHeaders, grid } = buildCalendarData({
    ...props,
    companyName: '',
    personAssignments: {},
    exactDates: {},
    companyUsers: [],
  }, { showAllEvents: true });

  return (
    <Document title={`Planning Vierge - Calendrier des Actions sur 12 Mois - ${year}`} author="D\u00C9CLARATIONS JURIDIQUES" subject="Planning strat\u00E9gique annuel - Vierge">
      <Page size={A4_PORTRAIT} style={[styles.portraitPage, styles.coverPage]}>
        <View style={styles.coverHeader}>
          <LogoPlaceholder size="large" />
          <Text style={styles.coverBrandName}>D{'\u00C9'}CLARATIONS JURIDIQUES</Text>
        </View>
        <View style={styles.coverContent}>
          <Text style={[styles.coverSubtitle, { fontFamily: 'Helvetica-Bold', fontSize: 14, color: ACCENT_BLUE, marginBottom: 4 }]}>Strat{'\u00E9'}gie Croissance</Text>
          <Text style={styles.coverTitle}>Calendrier des Actions sur 12 Mois</Text>
          <Text style={blankStyles.coverBlankNotice}>PLANNING VIERGE {'\u2014'} {'\u00C0'} REMPLIR</Text>
          <Text style={styles.coverSubtitle}>Ann{'\u00E9'}e {year}</Text>
          <Text style={{ fontSize: 10, color: '#6b7280', marginTop: 14 }}>Cabinet / Entreprise :</Text>
          <Text style={blankStyles.coverCompanyLine}> </Text>
          <Text style={styles.coverMeta}>{`G\u00E9n\u00E9r\u00E9 le : ${generatedAt}`}</Text>
        </View>
        <View style={styles.coverFooter}>
          <Text style={styles.coverFooterText}>
            Planning vierge {'\u2014'} Compl{'\u00E9'}tez les responsables et les dates exactes pour chaque action
          </Text>
        </View>
      </Page>

      <Page size={A4_LANDSCAPE} style={styles.page} wrap>
        <View style={styles.header} fixed>
          <View style={styles.headerLeft}>
            <LogoPlaceholder size="small" />
            <View>
              <Text style={styles.headerTitle}>Planning Vierge {'\u2014'} Calendrier des Actions sur 12 Mois - {year}</Text>
            </View>
          </View>
        </View>

        <Text style={styles.calendarTitle}>Planning des Actions sugg{'\u00E9'}r{'\u00E9'}es {'\u2014'} {'\u00C0'} remplir dans la section {'\u00AB'} Plan d{'\u2019'}actions {'\u2014'} {'\u00C0'} compl{'\u00E9'}ter {'\u00BB'}</Text>

        <View style={styles.tableContainer}>
          <View style={styles.tableRow}>
            <View style={[styles.rowLabel, { backgroundColor: HEADER_BG, borderBottomColor: '#d1d5db' }]}>
              <Text style={[styles.rowLabelText, { color: '#ffffff' }]}>Action</Text>
            </View>
            {monthHeaders.map((mh, i) => (
              <View key={i} style={[styles.monthHeaderCell, i === 11 ? { borderRightWidth: 0 } : {}]}>
                <Text style={styles.monthHeaderText}>{mh}</Text>
              </View>
            ))}
          </View>

          {visibleRows.map((row, rowIdx) => {
            const isAlt = rowIdx % 2 === 1;

            if (row.continuous) {
              return (
                <View key={row.key} style={[styles.tableRow, isAlt ? styles.tableRowAlt : {}]}>
                  <View style={styles.rowLabel}>
                    <Text style={styles.rowLabelText}>{row.label}</Text>
                    <Text style={blankStyles.blankPersonLine}>Resp. : {BLANK_LINE}</Text>
                  </View>
                  <View style={styles.continuousBandRow}>
                    <View style={styles.continuousBand}>
                      <Text style={styles.continuousBandText}>{row.continuousLabel}</Text>
                    </View>
                  </View>
                </View>
              );
            }

            const cells = grid[row.key] || Array.from({ length: 12 }, () => []);

            return (
              <View key={row.key} style={[styles.tableRow, isAlt ? styles.tableRowAlt : {}]} wrap={false}>
                <View style={styles.rowLabel}>
                  <Text style={styles.rowLabelText}>{row.label}</Text>
                  <Text style={blankStyles.blankPersonLine}>Resp. : {BLANK_LINE}</Text>
                </View>
                {cells.map((eventsInCell, colIdx) => (
                  <View key={colIdx} style={[styles.monthCell, colIdx === 11 ? { borderRightWidth: 0 } : {}]}>
                    {eventsInCell.map((evt, evtIdx) => (
                      <View key={evtIdx} style={[blankStyles.blankBadge, { borderColor: evt.color, opacity: evt.optional ? 0.5 : 1 }]}>
                        <Text style={[styles.eventBadgeText, { color: evt.color }]}>
                          {evt.theme || evt.label}
                        </Text>
                        {evt.optional && (
                          <Text style={{ fontSize: 4, color: '#9ca3af', textAlign: 'center' }}>(optionnel)</Text>
                        )}
                        <Text style={blankStyles.blankDateText}>__ / __</Text>
                      </View>
                    ))}
                  </View>
                ))}
              </View>
            );
          })}
        </View>

        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>

      <Page size={A4_PORTRAIT} style={styles.portraitPage} wrap>
        <View style={[styles.header, { marginBottom: 10 }]} fixed>
          <View style={styles.headerLeft}>
            <LogoPlaceholder size="small" />
            <View>
              <Text style={styles.headerTitle}>Planning Vierge {'\u2014'} Plan d{'\u2019'}actions - {year}</Text>
            </View>
          </View>
        </View>

        <Text style={styles.reportSectionTitle}>Plan d{'\u2019'}actions {'\u2014'} {'\u00C0'} compl{'\u00E9'}ter</Text>

        {ACTION_SECTIONS.map((section, idx) => (
          <BlankReportActionBlock
            key={section.rowKey}
            section={section}
            actionIndex={idx + 1}
            eventDates={getBlankEventDates(section.rowKey, props, { showAllEvents: true })}
          />
        ))}

        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>

      <Page size={A4_PORTRAIT} style={styles.portraitPage} wrap>
        <View style={[styles.header, { marginBottom: 10 }]} fixed>
          <View style={styles.headerLeft}>
            <LogoPlaceholder size="small" />
            <View>
              <Text style={styles.headerTitle}>Planning Vierge {'\u2014'} Attribution des responsabilit{'\u00E9'}s - {year}</Text>
            </View>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Fiche d{'\u2019'}attribution des responsabilit{'\u00E9'}s</Text>

        <Text style={[styles.reportBody, { marginBottom: 12 }]}>
          Compl{'\u00E9'}tez le tableau ci-dessous en indiquant le nom du responsable d{'\u00E9'}sign{'\u00E9'} pour chaque action ainsi que les dates pr{'\u00E9'}vues de r{'\u00E9'}alisation.
        </Text>

        <View style={blankStyles.attributionTable}>
          <View style={blankStyles.attributionHeaderRow}>
            <View style={[blankStyles.attributionHeaderCell, { width: 160 }]}>
              <Text style={blankStyles.attributionHeaderText}>Action</Text>
            </View>
            <View style={[blankStyles.attributionHeaderCell, { flex: 1 }]}>
              <Text style={blankStyles.attributionHeaderText}>Responsable</Text>
            </View>
            <View style={[blankStyles.attributionHeaderCell, { flex: 1 }]}>
              <Text style={blankStyles.attributionHeaderText}>P{'\u00E9'}riode sugg{'\u00E9'}r{'\u00E9'}e</Text>
            </View>
            <View style={[blankStyles.attributionHeaderCell, { flex: 1, borderRightWidth: 0 }]}>
              <Text style={blankStyles.attributionHeaderText}>Date exacte retenue</Text>
            </View>
          </View>

          {ROW_CONFIGS.map((row) => {
            const evtDates = getBlankEventDates(row.key, props, { showAllEvents: true });
            const showSubRows = (row.eventType === 'referent' || row.eventType === 'sci-referent') && evtDates.events.length > 1;

            if (showSubRows) {
              return evtDates.events.map((evt, evtIdx) => (
                <View key={`${row.key}-${evtIdx}`} style={[blankStyles.attributionRow, evt.optional ? { opacity: 0.6 } : {}]}>
                  <View style={[blankStyles.attributionCell, { width: 160 }]}>
                    <Text style={blankStyles.attributionLabelText}>
                      {evtIdx === 0 ? row.label : ' '}
                    </Text>
                    <Text style={{ fontSize: 6.5, color: '#6b7280', marginTop: 1 }}>
                      {evt.label}{evt.optional ? ' (optionnel)' : ''}
                    </Text>
                  </View>
                  <View style={[blankStyles.attributionCell, { flex: 1 }]}>
                    <Text style={blankStyles.attributionBlankText}>{BLANK_LINE}</Text>
                  </View>
                  <View style={[blankStyles.attributionCell, { flex: 1 }]}>
                    <Text style={[blankStyles.attributionBlankText, { color: '#374151', fontSize: 6.5 }]}>{evt.period}</Text>
                  </View>
                  <View style={[blankStyles.attributionCell, { flex: 1, borderRightWidth: 0 }]}>
                    <Text style={blankStyles.attributionBlankText}>{BLANK_DATE}</Text>
                  </View>
                </View>
              ));
            }

            const periodText = evtDates.continuous
              ? evtDates.continuousLabel ?? '(continue)'
              : evtDates.events.map((e) => e.period).join(', ') || '\u00C0 planifier';
            return (
              <View key={row.key} style={blankStyles.attributionRow}>
                <View style={[blankStyles.attributionCell, { width: 160 }]}>
                  <Text style={blankStyles.attributionLabelText}>{row.label}</Text>
                </View>
                <View style={[blankStyles.attributionCell, { flex: 1 }]}>
                  <Text style={blankStyles.attributionBlankText}>{BLANK_LINE}</Text>
                </View>
                <View style={[blankStyles.attributionCell, { flex: 1 }]}>
                  <Text style={[blankStyles.attributionBlankText, { color: '#374151', fontSize: 6.5 }]}>{periodText}</Text>
                </View>
                <View style={[blankStyles.attributionCell, { flex: 1, borderRightWidth: 0 }]}>
                  <Text style={blankStyles.attributionBlankText}>{evtDates.continuous ? '(continue)' : BLANK_DATE}</Text>
                </View>
              </View>
            );
          })}
        </View>

        <Text style={[styles.reportBody, { marginTop: 16 }]}>
          <Text style={styles.reportBodyBold}>Signature : </Text>
          <Text>{BLANK_LINE}{'                    '}</Text>
          <Text style={styles.reportBodyBold}>Date : </Text>
          <Text>{BLANK_DATE}</Text>
        </Text>

        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>
    </Document>
  );
};

export async function downloadBlankPlanningPdf(props: BlankPlanningPdfProps): Promise<void> {
  const doc = <BlankPlanningPdfDocument {...props} />;
  const blob = await pdf(doc).toBlob();
  const pdfBlob = new Blob([blob], { type: 'application/pdf' });

  const today = new Date().toISOString().split('T')[0];
  const filename = `planning_vierge_${props.year}_${today}.pdf`;

  const url = URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.type = 'application/pdf';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function downloadPlanningPdf(props: PlanningPdfProps): Promise<void> {
  const doc = <PlanningPdfDocument {...props} />;
  const blob = await pdf(doc).toBlob();
  const pdfBlob = new Blob([blob], { type: 'application/pdf' });

  const today = new Date().toISOString().split('T')[0];
  const filename = `planning_actions_${props.year}_${today}.pdf`;

  const url = URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.type = 'application/pdf';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
